import { Handle, Position, Edge } from 'reactflow';
import {
    Trash2, Bot, Mic, Timer, Languages, Phone, AlertTriangle,
    Settings, ArrowRight, MessageSquare, FileText as FileTextIcon, RotateCw, History, Trash,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import { useState } from 'react';
import AssistantDestinationSettings from './AssistantDestinationSettings';

interface AssistantNodeProps {
    data: {
        label: string;
        assistant: any;
        isDeleted?: boolean;
        isFirst?: boolean;
        voiceName?: string;
        language?: string;
        maxDurationSeconds?: number;
        recordingEnabled?: boolean;
        firstMessageMode?: string;
        destinations?: Array<{
            type: string;
            assistantName: string;
            message: string;
            description: string;
            transferMode: 'rolling-history' | 'swap-system-message-in-history' | 'delete-history';
        }>;
        onDelete: () => void;
        onDestinationSettingsChange: (settings: {
            assistantName: string;
            message: string;
            description: string;
            transferMode: 'rolling-history' | 'swap-system-message-in-history' | 'delete-history';
        }) => void;
    };
    id: string;
    edges: Edge[];
    assistants: Array<{
        id: string;
        name: string;
    }>;
}

const AssistantNode = ({ data, id, edges, assistants }: AssistantNodeProps) => {
    const [showSettings, setShowSettings] = useState(false);
    const [selectedDestination, setSelectedDestination] = useState<string | null>(null);
    const isEntry = data.isFirst;

    const handleDelete = (e: React.MouseEvent) => {
        e.stopPropagation();
        data.onDelete();
    };

    // Check for invalid destinations by comparing with actual edges
    const hasInvalidDestination = data.destinations?.some(destination => {
        // Skip the check if there are no destinations
        if (!data.destinations?.length) return false;

        const targetAssistant = assistants.find(a => a.name === destination.assistantName);
        // Only consider it invalid if:
        // 1. The target assistant exists (to avoid false positives during deletion)
        // 2. There's no edge connecting to that assistant
        return targetAssistant && !edges?.some(edge =>
            edge.source === id && edge.target === targetAssistant.id
        );
    }) || false;

    //   const destination = data.destinations?.[0];

    //   // Only log when destination changes
    //   useEffect(() => {
    //     if (destination) {
    //       console.debug('Destination settings updated:', destination);
    //     }
    //   }, [destination?.message, destination?.description, destination?.transferMode]);

    return (
        <>
            <div className={cn(
                "group relative",
                "px-4 pt-3 pb-3",
                "rounded-xl shadow-lg border-2",
                "bg-gradient-to-br from-background via-card to-background",
                "transition-all duration-200 ease-in-out",
                "hover:shadow-xl hover:scale-[1.02]",
                data.isDeleted ? "min-w-[320px] max-w-[320px]" : "min-w-[280px] max-w-[280px]",
                data.isDeleted ? "border-orange-400/50 from-orange-50/30 via-orange-50/20 to-orange-50/30" : "",
                data.isFirst
                    ? "border-primary/60 border-[1px] shadow-primary/10 ring-1 ring-primary/5"
                    : "border-border/50 hover:border-primary/30",
                hasInvalidDestination && "border-destructive/50 bg-destructive/5",
            )}>
                {/* Left Handle (Input) */}
                <Handle
                    type="target"
                    position={Position.Left}
                    className={cn(
                        "!h-3 !w-3 !-left-1.5",
                        "!border-2 !border-border",
                        "!bg-background",
                        "transition-colors duration-200",
                        "hover:!border-primary/50",
                        isEntry && "hidden"
                    )}
                />

                {/* Delete Button */}
                <div className={cn(
                    "absolute -right-2 -top-2",
                    "opacity-0 group-hover:opacity-100",
                    "transition-opacity duration-200"
                )}>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Button
                                variant="destructive"
                                size="icon"
                                className="h-6 w-6"
                                onClick={handleDelete}
                            >
                                <Trash2 className="h-3 w-3" />
                            </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                            <p>Delete Node</p>
                        </TooltipContent>
                    </Tooltip>
                </div>

                {/* Header Section */}
                <div className="flex items-start gap-3">
                    <div className={cn(
                        "p-2 rounded-lg",
                        "bg-gradient-to-br shadow-sm",
                        data.isDeleted
                            ? "from-orange-400/20 to-orange-400/10 text-orange-600"
                            : isEntry
                                ? "from-primary/20 to-primary/10 text-primary"
                                : "from-muted to-muted/80 text-muted-foreground"
                    )}>
                        {data.isDeleted ? <AlertTriangle className="w-4 h-4" /> : <Bot className="w-4 h-4" />}
                    </div>
                    <div className="space-y-1 flex-1 min-w-0">
                        <h3 className={cn(
                            "font-semibold leading-none pr-4",
                            data.isDeleted && "text-orange-700"
                        )}>
                            {data.isDeleted ? 'Deleted Assistant' : data.label}
                        </h3>
                        {data.isDeleted ? (
                            <div className="flex items-center gap-1.5 text-xs text-orange-600/80 font-mono whitespace-nowrap overflow-hidden">
                                <span>{data.assistant?.id ?? 'Unknown ID'}</span>
                            </div>
                        ) : (
                            data.voiceName && (
                                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                                    <Mic className="w-3 h-3 flex-shrink-0" />
                                    <span className="truncate">{data.voiceName}</span>
                                </div>
                            )
                        )}
                    </div>
                </div>

                {!data.isDeleted && (
                    <>
                        {/* Divider */}
                        <div className="h-px bg-gradient-to-r from-transparent via-border/50 to-transparent mb-3" />

                        {/* Info Grid */}
                        <div className="grid grid-cols-2 gap-x-3 gap-y-2 text-xs text-muted-foreground">
                            {data.language && (
                                <div className="flex items-center gap-1.5">
                                    <div className="p-1 rounded bg-muted/50">
                                        <Languages className="w-3 h-3" />
                                    </div>
                                    <span className="truncate">{data.language}</span>
                                </div>
                            )}
                            {data.maxDurationSeconds && (
                                <div className="flex items-center gap-1.5">
                                    <div className="p-1 rounded bg-muted/50">
                                        <Timer className="w-3 h-3" />
                                    </div>
                                    <span>{Math.floor(data.maxDurationSeconds / 60)}m max call</span>
                                </div>
                            )}
                            {data.recordingEnabled && (
                                <div className="flex items-center gap-1.5">
                                    <div className="p-1 rounded bg-muted/50">
                                        <Phone className="w-3 h-3" />
                                    </div>
                                    <span>Recording on</span>
                                </div>
                            )}
                            {data.firstMessageMode && (
                                <div className="flex items-center gap-1.5">
                                    <div className="p-1 rounded bg-muted/50">
                                        <MessageSquare className="w-3 h-3" />
                                    </div>
                                    <span>
                                        {data.firstMessageMode === 'assistant-speaks-first' 
                                            ? 'Speaks First'
                                            : data.firstMessageMode === 'assistant-waits-for-user' 
                                                ? 'Waits for User to Speak'
                                                : data.firstMessageMode}
                                    </span>
                                </div>
                            )}
                        </div>

                        {/* Destination Settings */}
                        <div className="mt-3 pt-3 border-t border-border/50">
                            <div className="flex items-center justify-between">
                                <span className="text-xs text-muted-foreground font-medium">
                                    Destinations
                                </span>
                            </div>
                            {hasInvalidDestination && (
                                <div className="mt-2 flex items-center gap-1.5 text-xs text-destructive">
                                    <AlertTriangle className="h-3 w-3" />
                                    <span>Invalid destination configuration detected</span>
                                </div>
                            )}
                            {edges.filter(edge => edge.source === id).length > 0 ? (
                                <div className="mt-2 space-y-1.5">
                                    {edges.filter(edge => edge.source === id).map(edge => {
                                        const targetAssistant = assistants.find(a => a.id === edge.target);
                                        if (!targetAssistant) return null;

                                        const destination = data.destinations?.find(d => d.assistantName === targetAssistant.name);

                                        return (
                                            <div key={edge.id} className="group flex items-center justify-between gap-2 p-1 rounded-md hover:bg-muted/50">
                                                <div className="flex-1 min-w-0 flex items-center gap-1">
                                                    <Bot className="h-2.5 w-2.5 text-primary/70" />
                                                    <span className="truncate text-[11px] font-medium text-muted-foreground/90">{targetAssistant.name}</span>
                                                    <ArrowRight className="h-2.5 w-2.5 text-muted-foreground/40" />
                                                </div>
                                                <div className="flex items-center gap-1">
                                                    {destination?.message && (
                                                        <Tooltip>
                                                            <TooltipTrigger>
                                                                <div className="p-1 rounded-md bg-primary/5 text-primary">
                                                                    <MessageSquare className="h-2.5 w-2.5" />
                                                                </div>
                                                            </TooltipTrigger>
                                                            <TooltipContent>
                                                                <p className="max-w-xs">{destination.message}</p>
                                                            </TooltipContent>
                                                        </Tooltip>
                                                    )}
                                                    {destination?.description && (
                                                        <Tooltip>
                                                            <TooltipTrigger>
                                                                <div className="p-1 rounded-md bg-primary/5 text-primary">
                                                                    <FileTextIcon className="h-2.5 w-2.5" />
                                                                </div>
                                                            </TooltipTrigger>
                                                            <TooltipContent>
                                                                <p className="max-w-xs">{destination.description}</p>
                                                            </TooltipContent>
                                                        </Tooltip>
                                                    )}
                                                    {destination && (
                                                        <Tooltip>
                                                            <TooltipTrigger>
                                                                <div className={cn(
                                                                    "p-1 rounded-md",
                                                                    "transition-colors duration-200",
                                                                    destination.transferMode === 'rolling-history' && "bg-blue-500/10 text-blue-500",
                                                                    destination.transferMode === 'swap-system-message-in-history' && "bg-amber-500/10 text-amber-500",
                                                                    destination.transferMode === 'delete-history' && "bg-red-500/10 text-red-500",
                                                                )}>
                                                                    {destination.transferMode === 'rolling-history' && <History className="h-2.5 w-2.5" />}
                                                                    {destination.transferMode === 'swap-system-message-in-history' && <RotateCw className="h-2.5 w-2.5" />}
                                                                    {destination.transferMode === 'delete-history' && <Trash className="h-2.5 w-2.5" />}
                                                                </div>
                                                            </TooltipTrigger>
                                                            <TooltipContent>
                                                                <div className="space-y-1">
                                                                    <p className="font-medium">
                                                                        {destination.transferMode === 'rolling-history' && 'Rolling History'}
                                                                        {destination.transferMode === 'swap-system-message-in-history' && 'Swap System Message'}
                                                                        {destination.transferMode === 'delete-history' && 'Delete History'}
                                                                    </p>
                                                                    <p className="text-xs text-muted-foreground">
                                                                        {destination.transferMode === 'rolling-history' && 'Keeps entire conversation history'}
                                                                        {destination.transferMode === 'swap-system-message-in-history' && 'Replaces original system message'}
                                                                        {destination.transferMode === 'delete-history' && 'Deletes conversation history'}
                                                                    </p>
                                                                </div>
                                                            </TooltipContent>
                                                        </Tooltip>
                                                    )}
                                                    <Tooltip>
                                                        <TooltipTrigger asChild>
                                                            <Button
                                                                variant="ghost"
                                                                size="icon"
                                                                className="h-6 w-6 opacity-0 group-hover:opacity-100 hover:bg-primary/10 hover:text-primary"
                                                                onClick={() => {
                                                                    setSelectedDestination(targetAssistant.name);
                                                                    setShowSettings(true);
                                                                }}
                                                            >
                                                                <Settings className="h-3 w-3" />
                                                            </Button>
                                                        </TooltipTrigger>
                                                        <TooltipContent>
                                                            <p>Edit Destination Settings</p>
                                                        </TooltipContent>
                                                    </Tooltip>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : (
                                <span className="text-xs text-muted-foreground/60 italic">
                                    No destinations
                                </span>
                            )}
                        </div>
                    </>
                )}


                {/* Right Handle (Output) */}
                <Handle
                    type="source"
                    position={Position.Right}
                    className={cn(
                        "!h-3 !w-3 !-right-1.5",
                        "!border-2 !border-border",
                        "!bg-background",
                        "transition-colors duration-200",
                        "hover:!border-primary/50"
                    )}
                />
            </div>
            {showSettings && selectedDestination && (
                <AssistantDestinationSettings
                    open={showSettings}
                    onOpenChange={setShowSettings}
                    settings={{
                        message: data.destinations?.find(d => d.assistantName === selectedDestination)?.message || '',
                        description: data.destinations?.find(d => d.assistantName === selectedDestination)?.description || '',
                        transferMode: data.destinations?.find(d => d.assistantName === selectedDestination)?.transferMode || 'rolling-history',
                    }}
                    onSettingsChange={(settings) => {
                        if (data.onDestinationSettingsChange) {
                            // Find existing destinations
                            const existingDestinations = data.destinations || [];
                            // Update or add the selected destination's settings
                            const updatedDestinations = existingDestinations.filter(d => d.assistantName !== selectedDestination);
                            updatedDestinations.push({
                                type: "assistant",
                                assistantName: selectedDestination,
                                ...settings,
                            });

                            // Update all destinations
                            data.onDestinationSettingsChange({
                                ...settings,
                                assistantName: selectedDestination,
                            });
                        }
                    }}
                    destinationName={selectedDestination}
                />
            )}
        </>
    );
};

export default AssistantNode;
